import { Button, Input } from '@mui/joy';

import { ReactNode, useState } from 'react';

import { Tooltip } from '@/design-system';

import { ManifestVariant } from './Manifest.utils';

export function ManifestKeyValuesEditor(props: {
  title?: ReactNode;
  value?: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabledMessage?: string;
  variant: ManifestVariant;
}) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editingKeyText, setEditingKeyText] = useState('');
  const [editingValueText, setEditingValueText] = useState('');

  const handleAdd = () => {
    if (key && value) {
      const newValue = { ...props.value, [key]: value };
      props.onChange(newValue);
      setKey('');
      setValue('');
      setIsAdding(false);
    }
  };

  const handleRemove = (keyToRemove: string) => {
    const newValue = { ...props.value };
    delete newValue[keyToRemove];
    props.onChange(newValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && key && value) {
      handleAdd();
    }
  };

  const handleUpdateKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) {
      setEditingKey(null);
      setEditingKeyText('');
      return;
    }
    const newValue = { ...props.value };
    const val = newValue[oldKey];
    delete newValue[oldKey];
    newValue[newKey] = val;
    props.onChange(newValue);
    setEditingKey(null);
    setEditingKeyText('');
  };

  const handleUpdateValue = (key: string, newValue: string) => {
    if (props.value?.[key] === newValue) {
      setEditingValue(null);
      setEditingValueText('');
      return;
    }
    props.onChange({
      ...props.value,
      [key]: newValue,
    });
    setEditingValue(null);
    setEditingValueText('');
  };

  const existingKeys = Object.keys(props.value || {});

  const addButtonTooltip = existingKeys.includes(key)
    ? 'Key already exists'
    : !key
    ? 'Key cannot be empty'
    : !value
    ? 'Value cannot be empty'
    : undefined;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{props.title}</div>
      </div>
      <div className="flex flex-col gap-2">
        {props.value && existingKeys.length > 0 && (
          <div className="flex flex-col gap-2">
            {Object.entries(props.value).map(([k, v]) => (
              <div key={k} className="flex flex-row gap-2">
                <div className="w-[200px]">
                  <Input
                    size="sm"
                    value={editingKey === k ? editingKeyText : k}
                    onChange={(e) => {
                      const newKey = e.target.value.replace(/\s/g, '');
                      setEditingKeyText(newKey);
                    }}
                    onBlur={(e) =>
                      handleUpdateKey(k, e.target.value.replace(/\s/g, ''))
                    }
                    onFocus={() => {
                      setEditingKey(k);
                      setEditingKeyText(k);
                    }}
                  />
                </div>
                <div className="w-[200px]">
                  <Input
                    size="sm"
                    value={editingValue === k ? editingValueText : v}
                    onChange={(e) => setEditingValueText(e.target.value)}
                    onBlur={(e) => handleUpdateValue(k, e.target.value)}
                    onFocus={() => {
                      setEditingValue(k);
                      setEditingValueText(v);
                    }}
                  />
                </div>
                <Tooltip title="Remove">
                  <button
                    type="button"
                    onClick={() => handleRemove(k)}
                    className="rounded-md px-2 py-0.5 text-secondary hover:bg-tertiary"
                  >
                    ×
                  </button>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
        {!isAdding && (
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            onClick={() => setIsAdding(true)}
            className="w-fit"
          >
            + Add
          </Button>
        )}
        {isAdding && (
          <div className="flex gap-2">
            <Input
              size="sm"
              placeholder="Key"
              value={key}
              onChange={(e) => setKey(e.target.value.replace(/\s/g, ''))}
              onKeyDown={handleKeyPress}
              className="w-[200px]"
            />
            <Input
              size="sm"
              placeholder="Value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-[200px]"
            />
            <Tooltip title={addButtonTooltip}>
              <div>
                <Button
                  size="sm"
                  onClick={handleAdd}
                  disabled={!key || !value || existingKeys.includes(key)}
                >
                  Add
                </Button>
              </div>
            </Tooltip>
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setKey('');
                setValue('');
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
